import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { KpiCard } from '../components/ui/KpiCard';
import { MapPin, Hash, TrendingUp, Clock, Timer } from 'lucide-react';
import BackLink from '../components/BackLink';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area, LineChart, Line,
} from 'recharts';
import { GRID, AXIS_PROPS, TOOLTIP_STYLE, ANIMATION } from '../utils/chartConfig';

const API = import.meta.env.VITE_API_URL || '';

function api(path) {
  return fetch(`${API}${path}`, { credentials: 'include' })
    .then(async (r) => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      return data;
    });
}

const DONUT_COLORS = ['#338edc', '#22D3EE', '#6366F1', '#F59E0B', '#EF4444'];

const STATUS_LABELS_DE = {
  offen: 'Offen',
  email_gesendet: 'E-Mail gesendet',
  beantwortet: 'Beantwortet',
  abgeschlossen: 'Abgeschlossen',
  fehler: 'Fehler',
};

const PERIOD_OPTIONS = [
  { value: 7, labelKey: 'days7' },
  { value: 30, labelKey: 'days30' },
  { value: 90, labelKey: 'days90' },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-elevated border border-line-hover rounded-lg shadow-modal p-3 text-xs">
      <p className="text-content-secondary font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-content">{p.name}: <strong>{p.value}</strong></span>
        </div>
      ))}
    </div>
  );
}

function DonutTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-surface-elevated border border-line-hover rounded-lg shadow-modal p-3 text-xs">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: d.payload.fill }} />
        <span className="text-content">{d.name}: <strong>{d.value}</strong></span>
      </div>
    </div>
  );
}

export default function AdressklaerungStats() {
  const { t, language } = useLanguage();
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState({});
  const [proTag, setProTag] = useState([]);
  const [statusVert, setStatusVert] = useState([]);
  const [trend, setTrend] = useState([]);
  const [topKunden, setTopKunden] = useState([]);
  const [loading, setLoading] = useState(true);

  const weeks = Math.max(2, Math.ceil(days / 7));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pt, sv, tr, tk] = await Promise.all([
        api(`/api/adressklaerungen/stats/overview?days=${days}`),
        api(`/api/adressklaerungen/stats/pro-tag?days=${days}`),
        api(`/api/adressklaerungen/stats/status-verteilung?days=${days}`),
        api(`/api/adressklaerungen/stats/loesungsrate-trend?weeks=${weeks}`),
        api(`/api/adressklaerungen/stats/top-kunden?limit=10&days=${days}`),
      ]);
      setOverview(ov);
      setProTag(pt);
      setStatusVert(sv.map(s => ({ ...s, name: STATUS_LABELS_DE[s.status] || s.status })));
      setTrend(tr);
      setTopKunden(tk);
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
    setLoading(false);
  }, [days, weeks]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fmtHours = (h) => {
    if (h == null || isNaN(h)) return '—';
    return `${Number(h).toFixed(1)}h`;
  };

  const loesungsrate = overview.loesungsrate ?? 0;
  const trendUp = loesungsrate >= 70 ? true : loesungsrate < 50 ? false : undefined;
  const trendLabel = loesungsrate >= 70 ? '> 70%' : loesungsrate < 50 ? '< 50%' : '';

  const kpiCards = [
    { label: t('adressklaerung.kpiGesamt'), value: overview.gesamt ?? 0, Icon: Hash, color: 'bg-[#EBF4FD] text-[#338edc]' },
    { label: t('adressklaerung.kpiLoesungsrate'), value: `${loesungsrate}%`, Icon: TrendingUp, trend: trendLabel, trendUp, color: 'bg-[#ECFDF5] text-[#10B981]' },
    { label: t('adressklaerung.kpiAvgAntwort'), value: fmtHours(overview.avg_antwortzeit_stunden), Icon: Clock, color: 'bg-[#ECFEFF] text-[#22D3EE]' },
    { label: t('adressklaerung.kpiAvgDauer'), value: fmtHours(overview.avg_gesamtdauer_stunden), Icon: Timer, color: 'bg-[#FFFBEB] text-[#F59E0B]' },
  ];

  // Format date for chart x-axis
  const fmtDate = (d) => {
    if (!d) return '';
    const parts = d.split('-');
    return `${parts[2]}.${parts[1]}`;
  };

  return (
    <div className="p-6">
      <BackLink to="/kunden/adressklaerungen" label={t('common.backToList')} className="mb-3" />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-button bg-primary-500/10 text-primary-600 dark:text-primary-400">
            <MapPin className="w-5 h-5" />
          </div>
          <h1 className="text-xl font-bold text-content">{t('adressklaerung.statsTitle')}</h1>
        </div>

        {/* Period Toggle */}
        <div className="flex items-center bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-button p-0.5">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setDays(opt.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-button transition-all ${
                days === opt.value
                  ? 'bg-white dark:bg-[var(--bg-card)] text-content font-semibold shadow-sm'
                  : 'text-content-secondary hover:text-content'
              }`}
            >
              {opt.value} {t('common.days') || 'Days'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {kpiCards.map((kpi, i) => (
              <KpiCard key={i} label={kpi.label} value={kpi.value} icon={kpi.Icon} trend={kpi.trend} trendUp={kpi.trendUp} color={kpi.color} />
            ))}
          </div>

          {/* Charts Row 1: Bar + Donut */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Bar: Klärungen pro Tag */}
            <div className="bg-surface-card border border-line-card rounded-card p-6">
              <h3 className="text-sm font-semibold text-content mb-4">{t('adressklaerung.chartProTag')}</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={proTag} {...ANIMATION}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="datum" {...AXIS_PROPS} tickFormatter={fmtDate} />
                  <YAxis {...AXIS_PROPS} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="anzahl" name={t('adressklaerung.colAnzahl')} fill="#22D3EE" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Donut: Status-Verteilung */}
            <div className="bg-surface-card border border-line-card rounded-card p-6">
              <h3 className="text-sm font-semibold text-content mb-4">{t('adressklaerung.chartStatus')}</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={statusVert}
                    dataKey="anzahl"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="55%"
                    outerRadius="80%"
                    paddingAngle={2}
                    {...ANIMATION}
                  >
                    {statusVert.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<DonutTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => <span className="text-xs text-content-secondary">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart Row 2: Lösungsrate Trend */}
          <div className="bg-surface-card border border-line-card rounded-card p-6 mb-4">
            <h3 className="text-sm font-semibold text-content mb-4">{t('adressklaerung.chartTrend')}</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trend} {...ANIMATION}>
                <defs>
                  <linearGradient id="violetGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#338edc" stopOpacity={0.1} />
                    <stop offset="100%" stopColor="#338edc" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="woche" {...AXIS_PROPS} />
                <YAxis {...AXIS_PROPS} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="loesungsrate"
                  name={t('adressklaerung.kpiLoesungsrate')}
                  stroke="#338edc"
                  strokeWidth={2}
                  fill="url(#violetGradient)"
                  dot={{ r: 4, fill: '#338edc', stroke: '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#338edc', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Top Kunden Table */}
          <div className="bg-surface-card border border-line-card rounded-card overflow-hidden">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-sm font-semibold text-content">{t('adressklaerung.chartTopKunden')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-inset">
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider w-12">{t('adressklaerung.colRang')}</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.colKunde')}</th>
                    <th className="text-right px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.colAnzahl')}</th>
                    <th className="text-right px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.colLoesungsrate')}</th>
                    <th className="text-right px-4 py-3 text-[11px] font-bold text-content-secondary uppercase tracking-wider">{t('adressklaerung.colAvgAntwort')}</th>
                  </tr>
                </thead>
                <tbody>
                  {topKunden.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8 text-content-secondary">{t('adressklaerung.noData')}</td></tr>
                  ) : topKunden.map((k, i) => (
                    <tr key={i} className="border-b border-line last:border-b-0 hover:bg-surface-hover transition-colors">
                      <td className="px-4 py-3 text-content-secondary font-medium">{i + 1}</td>
                      <td className="px-4 py-3 text-content font-medium">{k.haendler_name}</td>
                      <td className="px-4 py-3 text-right text-content">{k.anzahl}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium ${
                          (k.loesungsrate ?? 0) >= 70 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          : (k.loesungsrate ?? 0) < 50 ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                          : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        }`}>
                          {k.loesungsrate ?? 0}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-content-secondary">{fmtHours(k.avg_antwortzeit_stunden)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
